import React, { useMemo, useState } from "react";
import { Box, Typography, useTheme } from "@mui/material";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Bar } from "react-chartjs-2";
import { useTranslation } from "react-i18next";
import { GRAPH_PALETTE } from "../theme/style/graphChartPalette";
import { SegmentedButton, ChartCard } from "../components/m3";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend, ChartDataLabels);

/**
 * NetZeroEmissionsBreakdownCharts
 * 
 * Houses companion visualizations:
 * - Graph 1: Scope Comparison (Year-wise grouped bar chart)
 * - Graph 2: Emissions Reduction Waterfall (Base Year -> S1 -> S2 -> S3 -> MRY)
 * - Graph 3: Absolute Scope Emissions Reduction (Horizontal diverging bar chart)
 */
function NetZeroEmissionsBreakdownCharts({
  baseYear = 2024,
  mostRecentYear = 2025,
  scope1Base = 0,
  scope2Base = 0,
  scope3Base = 0,
  scope1Mry = 0,
  scope2Mry = 0,
  scope3Mry = 0,
  unit = "tCO₂e",
}) {
  const { t } = useTranslation();
  const theme = useTheme();
  const isDark = theme.palette.mode === "dark";
  const axisColors = isDark ? GRAPH_PALETTE.axes.darkMode : GRAPH_PALETTE.axes.lightMode;

  const [activeChart, setActiveChart] = useState("scope-comparison");

  const s1B = Number(scope1Base) || 0;
  const s2B = Number(scope2Base) || 0;
  const s3B = Number(scope3Base) || 0;
  const totalBase = s1B + s2B + s3B;

  const s1M = Number(scope1Mry) || 0;
  const s2M = Number(scope2Mry) || 0;
  const s3M = Number(scope3Mry) || 0;
  const totalMry = s1M + s2M + s3M;

  // Reductions = Base - MRY (positive = reduction, negative = increase)
  const diffS1 = s1B - s1M;
  const diffS2 = s2B - s2M;
  const diffS3 = s3B - s3M;
  const diffTotal = totalBase - totalMry;

  // -------------------------------------------------------------
  // GRAPH 1: Grouped Bar Chart (Emissions by Scope)
  // -------------------------------------------------------------
  const scopeComparisonData = useMemo(() => {
    return {
      labels: [
        t("Scope 1 (Direct)"),
        t("Scope 2 (Indirect - Purchased Energy)"),
        t("Scope 3 (Other Indirect)"),
      ],
      datasets: [
        {
          label: `${t("Base Year")} (${baseYear})`,
          data: [s1B, s2B, s3B],
          backgroundColor: ["#dc2626", "#d97706", "#0284c7"],
          borderColor: ["#b91c1c", "#b45309", "#0369a1"],
          borderWidth: 1,
          borderRadius: 4,
        },
        {
          label: `${t("MRY")} (${mostRecentYear})`,
          data: [s1M, s2M, s3M],
          backgroundColor: ["#fca5a5", "#fde68a", "#bae6fd"],
          borderColor: ["#ef4444", "#f59e0b", "#38bdf8"],
          borderWidth: 1,
          borderRadius: 4,
        },
      ],
    };
  }, [baseYear, mostRecentYear, s1B, s2B, s3B, s1M, s2M, s3M, t]);

  const scopeComparisonOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        datalabels: {
          display: true,
          color: isDark ? "#ffffff" : "#1b1c1a",
          anchor: "end",
          align: "top",
          offset: 2,
          font: { family: GRAPH_PALETTE.font.family, size: 11, weight: 600 },
          formatter: (val) => {
            if (val === null || val === undefined || val === 0) return "";
            return Number(val).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
          },
        },
        legend: {
          position: "bottom",
          labels: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 12, weight: 500 },
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => ` ${ctx.dataset.label}: ${Number(ctx.raw).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`,
          },
        },
      },
      scales: {
        x: {
          grid: { color: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
          ticks: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 12, weight: 500 },
          },
        },
        y: {
          grid: { color: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
          ticks: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 11 },
            callback: (val) => Number(val).toLocaleString(),
          },
          title: {
            display: true,
            text: `${t("Emissions")} (${unit})`,
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 12, weight: 600 },
          },
        },
      },
    };
  }, [isDark, axisColors, unit, t]);

  // -------------------------------------------------------------
  // GRAPH 2: Waterfall Chart (Base -> S1 -> S2 -> S3 -> MRY)
  // -------------------------------------------------------------
  const waterfallData = useMemo(() => {
    // Step positions using Chart.js floating bars [bottom, top]
    // 1. Base Total: [0, totalBase]
    // 2. S1 Change: from totalBase to totalBase - diffS1
    const p1 = totalBase;
    const p2 = p1 - diffS1;
    const p3 = p2 - diffS2;
    const p4 = p3 - diffS3; // should equal totalMry

    const steps = [
      {
        label: `${baseYear} ${t("Total")}`,
        range: [0, totalBase],
        color: "#64748b",
        diff: totalBase,
      },
      {
        label: `${t("Scope 1")} ${diffS1 >= 0 ? t("Reduction") : t("Increase")}`,
        range: [Math.min(p1, p2), Math.max(p1, p2)],
        color: diffS1 >= 0 ? "#ef4444" : "#22c55e",
        diff: -diffS1,
      },
      {
        label: `${t("Scope 2")} ${diffS2 >= 0 ? t("Reduction") : t("Increase")}`,
        range: [Math.min(p2, p3), Math.max(p2, p3)],
        color: "#f59e0b",
        diff: -diffS2,
      },
      {
        label: `${t("Scope 3")} ${diffS3 >= 0 ? t("Reduction") : t("Increase")}`,
        range: [Math.min(p3, p4), Math.max(p3, p4)],
        color: "#0ea5e9",
        diff: -diffS3,
      },
      {
        label: `${mostRecentYear} ${t("Total")}`,
        range: [0, totalMry],
        color: "#16a34a",
        diff: totalMry,
      },
    ];

    return {
      labels: steps.map((s) => s.label),
      datasets: [
        {
          label: t("Emissions"),
          data: steps.map((s) => s.range),
          backgroundColor: steps.map((s) => s.color),
          borderRadius: 4,
          borderWidth: 1,
          borderColor: isDark ? "rgba(255,255,255,0.2)" : "rgba(0,0,0,0.1)",
        },
      ],
      stepDetails: steps,
    };
  }, [baseYear, mostRecentYear, totalBase, totalMry, diffS1, diffS2, diffS3, isDark, t]);

  const waterfallOptions = useMemo(() => {
    return {
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: {
          display: true,
          color: (ctx) => {
            const step = waterfallData.stepDetails[ctx.dataIndex];
            if (!step) return "#ffffff";
            const diff = Math.abs(step.diff);
            // Thin bars (like tiny Scope 2 changes) show dark text outside, normal bars show white text inside
            return diff < 150 ? (isDark ? "#ffffff" : "#1b1c1a") : "#ffffff";
          },
          anchor: (ctx) => {
            const step = waterfallData.stepDetails[ctx.dataIndex];
            if (!step) return "center";
            const diff = Math.abs(step.diff);
            return diff < 150 ? "end" : "center";
          },
          align: (ctx) => {
            const step = waterfallData.stepDetails[ctx.dataIndex];
            if (!step) return "center";
            const diff = Math.abs(step.diff);
            return diff < 150 ? "top" : "center";
          },
          offset: (ctx) => {
            const step = waterfallData.stepDetails[ctx.dataIndex];
            if (!step) return 0;
            return Math.abs(step.diff) < 150 ? 4 : 0;
          },
          font: { family: GRAPH_PALETTE.font.family, size: 11, weight: 600 },
          formatter: (val, ctx) => {
            const step = waterfallData.stepDetails[ctx.dataIndex];
            if (!step) return "";
            const num = Number(step.diff);
            const isPillar = ctx.dataIndex === 0 || ctx.dataIndex === waterfallData.stepDetails.length - 1;
            const sign = isPillar ? "" : (num >= 0 ? "+" : "");
            return `${sign}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const step = waterfallData.stepDetails[ctx.dataIndex];
              const range = ctx.raw;
              const span = Math.abs(range[1] - range[0]);
              return ` ${step.label}: ${span.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: { color: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
          ticks: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 11, weight: 500 },
          },
        },
        y: {
          grid: { color: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
          ticks: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 11 },
            callback: (val) => Number(val).toLocaleString(),
          },
          title: {
            display: true,
            text: `${t("Emissions")} (${unit})`,
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 12, weight: 600 },
          },
        },
      },
    };
  }, [waterfallData, isDark, axisColors, unit, t]);

  // -------------------------------------------------------------
  // GRAPH 3: Absolute Scope Reductions (Horizontal Diverging Bar)
  // -------------------------------------------------------------
  const divergingData = useMemo(() => {
    const categories = [
      { label: t("Scope 1"), value: diffS1, color: "#ef4444" },
      { label: t("Scope 2"), value: diffS2, color: "#f59e0b" },
      { label: t("Scope 3"), value: diffS3, color: "#0ea5e9" },
      { label: t("Total (Reported)"), value: diffTotal, color: "#16a34a" },
    ];

    return {
      labels: categories.map((c) => c.label),
      datasets: [
        {
          label: `${t("Reduction")} (${unit})`,
          data: categories.map((c) => c.value),
          backgroundColor: categories.map((c) => c.color),
          borderRadius: 4,
          borderWidth: 1,
          borderColor: isDark ? "rgba(255,255,255,0.15)" : "rgba(0,0,0,0.1)",
        },
      ],
    };
  }, [diffS1, diffS2, diffS3, diffTotal, isDark, unit, t]);

  const divergingOptions = useMemo(() => {
    return {
      indexAxis: "y",
      responsive: true,
      maintainAspectRatio: false,
      plugins: {
        legend: { display: false },
        datalabels: {
          display: true,
          color: (ctx) => {
            const val = Math.abs(Number(ctx.dataset.data[ctx.dataIndex]));
            return val < 100 ? (isDark ? "#ffffff" : "#1b1c1a") : "#ffffff";
          },
          anchor: (ctx) => {
            const val = Number(ctx.dataset.data[ctx.dataIndex]);
            if (Math.abs(val) < 100) return val >= 0 ? "end" : "start";
            return "center";
          },
          align: (ctx) => {
            const val = Number(ctx.dataset.data[ctx.dataIndex]);
            if (Math.abs(val) < 100) return val >= 0 ? "right" : "left";
            return "center";
          },
          offset: (ctx) => {
            const val = Math.abs(Number(ctx.dataset.data[ctx.dataIndex]));
            return val < 100 ? 6 : 0;
          },
          font: { family: GRAPH_PALETTE.font.family, size: 11, weight: 600 },
          formatter: (val) => {
            const num = Number(val);
            const sign = num > 0 ? "+" : "";
            return `${sign}${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
          },
        },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const val = Number(ctx.raw);
              const sign = val > 0 ? "+" : "";
              return ` ${ctx.label}: ${sign}${val.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${unit}`;
            },
          },
        },
      },
      scales: {
        x: {
          grid: {
            color: (ctx) => (ctx.tick.value === 0 ? (isDark ? "#ffffff" : "#000000") : isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)"),
            lineWidth: (ctx) => (ctx.tick.value === 0 ? 2 : 1),
          },
          ticks: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 11 },
            callback: (val) => Number(val).toLocaleString(),
          },
          title: {
            display: true,
            text: `${t("Net Reduction / (Increase)")} (${unit})`,
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 12, weight: 600 },
          },
        },
        y: {
          grid: { color: isDark ? "rgba(255,255,255,0.06)" : "rgba(0,0,0,0.06)" },
          ticks: {
            color: axisColors.tick,
            font: { family: GRAPH_PALETTE.font.family, size: 12, weight: 500 },
          },
        },
      },
    };
  }, [isDark, axisColors, unit, t]);

  return (
    <ChartCard
      title={t("Emissions Scope Analytics")}
      actions={
        <SegmentedButton
          options={[
            { value: "scope-comparison", label: t("Scope Breakdown") },
            { value: "waterfall", label: t("Reduction Waterfall") },
            { value: "diverging", label: t("Absolute Reductions") },
          ]}
          value={activeChart}
          onChange={setActiveChart}
        />
      }
    >
      <Box sx={{ width: "100%", height: 380, position: "relative" }}>
        {activeChart === "scope-comparison" && (
          <Bar data={scopeComparisonData} options={scopeComparisonOptions} />
        )}
        {activeChart === "waterfall" && (
          <Bar data={waterfallData} options={waterfallOptions} />
        )}
        {activeChart === "diverging" && (
          <Bar data={divergingData} options={divergingOptions} />
        )}
      </Box>
    </ChartCard>
  );
}

export default NetZeroEmissionsBreakdownCharts;
